import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Typography,
  Box,
  Stack,
  Divider,
  Chip,
} from '@mui/material';
import {
  InfoOutlined,
  WarningAmberOutlined,
  ErrorOutlineOutlined,
} from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import useGetPrograms from '../../Hooks/useGetPrograms';
import useGetLocations from '../../Hooks/useGetLocation';
import usePermissions from '../../Reducers/usePermissions';
import { ADMIN, ROOT } from '../../Static/static';
import { useGetAnnouncements } from '../../Hooks/useGetAnnouncements';
import LocationSelect from '../Select/LocationSelect';
import ProgramSelect from '../Select/ProgramSelect';

// ─── Validation ───────────────────────────────────────────────────────────────

const validationSchema = Yup.object({
  id:              Yup.number().nullable(),
  title:           Yup.string().required('Title is required').max(255, 'Max 255 characters'),
  body:            Yup.string().required('Body is required').max(1000, 'Max 1000 characters'),
  location_id:     Yup.number().nullable(),
  severity:        Yup.string().required('Severity is required').oneOf(['info', 'warning', 'error']),
  organization_id: Yup.number().required('Organization ID is required'),
  program_id:      Yup.number().nullable(),
  admin_id:        Yup.number().nullable(),
  staff_id:        Yup.number().nullable(),
});

// ─── Severity options ─────────────────────────────────────────────────────────

const SEVERITY_OPTIONS = [
  {
    value:   'info',
    label:   'Info',
    sub:     'General update',
    icon:    InfoOutlined,
    color:   'info',
    chipBg:  'info.50',
    chipFg:  'info.800',
    borderActive: 'info.main',
    bgActive:'info.50',
  },
  {
    value:   'warning',
    label:   'Warning',
    sub:     'Action may be needed',
    icon:    WarningAmberOutlined,
    color:   'warning',
    chipBg:  'warning.50',
    chipFg:  'warning.800',
    borderActive: 'warning.main',
    bgActive:'warning.50',
  },
  {
    value:   'error',
    label:   'Urgent',
    sub:     'Immediate attention',
    icon:    ErrorOutlineOutlined,
    color:   'error',
    chipBg:  'error.50',
    chipFg:  'error.800',
    borderActive: 'error.main',
    bgActive:'error.50',
  },
];

// ─── Severity picker ──────────────────────────────────────────────────────────

function SeverityPicker({ value, onChange }) {
  const active = SEVERITY_OPTIONS.find((o) => o.value === value) ?? SEVERITY_OPTIONS[0];

  return (
    <Box>
      {/* Three-way toggle */}
      <Stack direction="row" spacing={0.75} sx={{ mb: 1 }}>
        {SEVERITY_OPTIONS.map((opt) => {
          const Icon     = opt.icon;
          const isActive = opt.value === value;
          return (
            <Box
              key={opt.value}
              onClick={() => onChange(opt.value)}
              sx={{
                flex:        1,
                display:     'flex',
                flexDirection:'column',
                alignItems:  'center',
                gap:         0.5,
                py:          1,
                px:          0.5,
                borderRadius:1.5,
                border:      '0.5px solid',
                borderColor: isActive ? opt.borderActive : 'divider',
                bgcolor:     isActive ? opt.bgActive : 'background.paper',
                cursor:      'pointer',
                transition:  'all 0.12s',
                '&:hover': {
                  bgcolor: isActive ? opt.bgActive : 'action.hover',
                },
                '&:active': { transform: 'scale(0.98)' },
              }}
            >
              <Icon
                sx={{
                  fontSize: 18,
                  color: isActive ? `${opt.color}.main` : 'text.secondary',
                }}
              />
              <Typography
                variant="caption"
                fontWeight={isActive ? 500 : 400}
                sx={{ color: isActive ? `${opt.color}.main` : 'text.secondary' }}
              >
                {opt.label}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {/* Active description chip */}
      <Chip
        label={active.sub}
        size="small"
        sx={{
          fontSize:  11,
          height:    20,
          bgcolor:   active.chipBg,
          color:     active.chipFg,
          border:    'none',
        }}
      />
    </Box>
  );
}

// ─── Character counter helper ─────────────────────────────────────────────────

function CharCount({ value = '', max }) {
  const remaining = max - value.length;
  return (
    <Typography
      variant="caption"
      sx={{
        display:   'block',
        textAlign: 'right',
        mt:        0.25,
        color:     remaining < 20 ? 'warning.main' : 'text.disabled',
      }}
    >
      {value.length} / {max}
    </Typography>
  );
}

// ─── Section label ────────────────────────────────────────────────────────────

function FieldLabel({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display:       'block',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontWeight:    500,
        color:         'text.secondary',
        mb:            0.5,
      }}
    >
      {children}
    </Typography>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const AddAnnouncement = ({ open, onClose, initialData }) => {
  const role = useSelector((state) => state.role);
  const { programs }  = useGetPrograms(role);
  const { locations, refetch } = useGetLocations(role);
  const { createAnnouncement, updateAnnouncement } = useGetAnnouncements();
  const { hasPermission } = usePermissions();

  const isEditing = Boolean(initialData?.id);

  const initialValues = {
    id:              initialData?.id              ?? null,
    title:           initialData?.title           ?? '',
    body:            initialData?.body            ?? '',
    location_id:     initialData?.location_id     ?? null,
    severity:        initialData?.severity        ?? 'info',
    organization_id: role.organization_id,
    program_id:      initialData?.program_id      ?? null,
    admin_id:        role.role === ROOT  ? role.id : null,
    staff_id:        role.role === ADMIN ? role.id : null,
  };

  const handleSubmit = async (values) => {
    if (isEditing) {
      const updated = await updateAnnouncement(values);
      if (updated) onClose();
    } else {
      const created = await createAnnouncement(values);
      if (created) onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
    >
      {/* ── Header ── */}
      <DialogTitle sx={{ pb: 1.5 }}>
        <Typography variant="h6" fontWeight={500}>
          {isEditing ? 'Edit announcement' : 'Add announcement'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Visible to tutors at the selected location or program
        </Typography>
      </DialogTitle>

      <Divider />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
          <Form>
            <DialogContent sx={{ pt: 2, pb: 1 }}>
              <Stack>

                {/* Title */}
                <Box>
                  <FieldLabel>Title</FieldLabel>
                  <TextField
                    fullWidth
                    size="small"
                    name="title"
                    placeholder="e.g. Campus closed April 18"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                    inputProps={{ maxLength: 255 }}
                  />
                  <CharCount value={values.title} max={255} />
                </Box>

                {/* Body */}
                <Box>
                  <FieldLabel>Body</FieldLabel>
                  <TextField
                    fullWidth
                    size="small"
                    name="body"
                    placeholder="Describe the announcement in full…"
                    value={values.body}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.body && Boolean(errors.body)}
                    helperText={touched.body && errors.body}
                    multiline
                    rows={6}
                    inputProps={{ maxLength: 1000 }}
                  />
                  <CharCount value={values.body} max={1000} />
                </Box>

                {/* Severity */}
                <Box>
                  <FieldLabel>Severity</FieldLabel>
                  <SeverityPicker
                    value={values.severity}
                    onChange={(v) => setFieldValue('severity', v)}
                  />
                  {touched.severity && errors.severity && (
                    <FormHelperText error>{errors.severity}</FormHelperText>
                  )}
                </Box>

                {/* Location + Program side by side */}
                <Stack direction="row" spacing={1}>

                  <LocationSelect
                    locations={locations}
                    value={values.location_id ?? ''}
                    onChange={(val) => setFieldValue('location_id', val)}
                    error={touched.location_id && Boolean(errors.location_id)}
                    helperText={touched.location_id && errors.location_id}
                    required
                  />

                  <ProgramSelect 
                    programs={programs}
                    value={values.program_id ?? ''}
                    onChange={(val) => setFieldValue('program_id', val)}
                    error={touched.program_id && Boolean(errors.program_id)}
                    helperText={touched.program_id && errors.program_id}
                    required
                  />
                </Stack>

              </Stack>
            </DialogContent>

            {/* ── Footer ── */}
            <DialogActions
              sx={{
                px: 2.5, py: 1.75,
                borderTop: '0.5px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  color="inherit"
                  onClick={onClose}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  disabled={!hasPermission('write', 'announcements')}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  {isEditing ? 'Save changes' : 'Post announcement'}
                </Button>
              </Stack>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddAnnouncement;